import { createFuzzySearch } from './fuzzy'

export function setupToggles() {
  const toggles = document.querySelectorAll('.dropdown-toggle')
  const dropdowns = document.querySelectorAll('.dropdown')
  const dropdownMenus = document.querySelectorAll('.dropdown-menu')

  function hideAll() {
    for (const m of dropdownMenus) {
      m.classList.add('hidden')
    }
    for (const d of dropdowns) {
      d.classList.remove('open')
    }
  }

  for (const toggle of toggles) {
    toggle.addEventListener('click', () => {
      const dropdown = toggle.closest('.dropdown')
      const menu = dropdown.querySelector('.dropdown-menu')

      const isHidden = menu.classList.contains('hidden')

      hideAll()

      if (isHidden) {
        menu.classList.remove('hidden')
        dropdown.classList.add('open')
      } else {
        menu.classList.add('hidden')
        dropdown.classList.remove('open')
      }
    })
  }

  document.addEventListener('click', (e) => {
    if (!e.target.closest('.dropdown')) {
      hideAll()
    }
  })

  const dataCloseBtns = document.querySelectorAll('[data-dropdown-close]')
  for (const button of dataCloseBtns) {
    button.addEventListener('click', () => {
      hideAll()
    })
  }
}

export function MultiCheckboxDropdown({
  containerId,
  items,
  type = '',
  labelResolver = (item) => item.label ?? item,
  valueResolver = (item) => item.value ?? item,
  resolveCollection,
  onUpdate,
  maxVisible = 2,
  groupBy = null,
  labelRenderer = null,
  searchMaxDistance = null,
}) {
  const container = document.getElementById(containerId)
  const labelsEl = container
    .closest('.dropdown')
    .querySelector('[data-dropdown-labels]')

  let filterDirty = false

  function applyIfDirty(update) {
    if (!filterDirty) {
      return
    }

    filterDirty = false
    update()
  }

  function renderCheckbox(item, itemType = type) {
    const id = `${itemType}-${valueResolver(item)}`
    return `
      <label for="${id}" class="group flex items-center gap-2 p-2 rounded cursor-pointer transition-colors text-white/80
        hover:bg-white/5
        has-[:checked]:bg-white/10
        has-[:checked]:text-white
        has-[:disabled]:opacity-40
        has-[:disabled]:cursor-not-allowed">
        <input
          id="${id}"
          ${itemType ? `data-filter="${itemType}"` : ''}
          value="${valueResolver(item)}"
          type="checkbox"
          class="accent-white"
        />
        ${labelRenderer ? labelRenderer(item) : `<span>${labelResolver(item)}</span>`}
      </label>`
  }

  const grouped = groupBy
    ? groupBy.map(({ label: groupLabel, type: filterType }) => {
        const groupItems = items
        return `
        <div class="flex flex-col gap-2 grow">
          <span class="text-white/50 text-xs font-semibold">${groupLabel}</span>
          ${groupItems.map((item) => renderCheckbox(item, filterType)).join('')}
        </div>`
      })
    : [
        `<div class="flex flex-col gap-2 grow">${items.map(renderCheckbox).join('')}</div>`,
      ]

  container.innerHTML = `
    <div class="flex gap-x-6 text-sm text-white/80 overflow-y-auto max-h-screen md:max-h-80 w-full">
      ${grouped.join('')}
    </div>
  `

  const checkboxes = Array.from(
    container.querySelectorAll('input[type=checkbox]')
  )

  checkboxes.forEach((checkbox) => {
    const collection = resolveCollection(checkbox)
    checkbox.checked = collection.includes(checkbox.value)
  })

  function updateLabels() {
    const checked = checkboxes.filter((c) => c.checked)

    const labelsList = checked.map((c) => {
      const item = items.find((i) => valueResolver(i) === c.value)
      return labelResolver(item)
    })

    let display = 'All'

    if (labelsList.length === 1) {
      labelsEl.textContent = labelsList[0]
      return
    }

    const shortLabels = []
    const longLabels = []

    labelsList.forEach((label) => {
      if (label.length > 10) {
        longLabels.push(label)
      } else {
        shortLabels.push(label)
      }
    })

    if (shortLabels.length === 0 && longLabels.length > 0) {
      const shortest = longLabels.reduce((a, b) =>
        a.length <= b.length ? a : b
      )

      const remaining = longLabels.length - 1
      display = shortest

      if (remaining > 0) {
        display += `, +${remaining}`
      }

      labelsEl.textContent = display
      return
    }

    let hiddenCount = longLabels.length

    if (shortLabels.length > 0) {
      const visible = shortLabels.slice(0, maxVisible)
      hiddenCount += shortLabels.length - visible.length

      display = visible.join(', ')

      if (hiddenCount > 0) {
        display += `, +${hiddenCount}`
      }
    }

    labelsEl.textContent = display
  }

  updateLabels()

  for (const checkbox of checkboxes) {
    checkbox.addEventListener('change', ({ currentTarget }) => {
      const collection = resolveCollection(currentTarget)
      const value = currentTarget.value
      const isChecked = currentTarget.checked
      const index = collection.indexOf(value)

      if (isChecked && index === -1) {
        collection.push(value)
        filterDirty = true
      } else if (!isChecked && index !== -1) {
        collection.splice(index, 1)
        filterDirty = true
      }

      updateLabels()
      applyIfDirty(onUpdate)
    })
  }

  const fuzzySearch = container.parentNode.querySelector('.fuzzy-search-bar')

  let searchInput
  if (fuzzySearch) {
    searchInput = createFuzzySearch({
      container: fuzzySearch,
      items: items.map(labelResolver),
      maxDistance: searchMaxDistance,
      onFilter: (matches) => {
        const matchSet = new Set(matches.map((m) => m.toLowerCase()))
        checkboxes.forEach((c) => {
          const item = items.find((i) => valueResolver(i) === c.value)
          const label = item ? labelResolver(item).toLowerCase() : ''
          c.parentNode.classList.toggle('hidden', !matchSet.has(label))
        })
      },
    })
  }

  return {
    updateLabels,
    getCheckboxes: () => checkboxes,
    reset: () => {
      checkboxes.forEach((cb) => {
        cb.checked = false
        cb.disabled = false
      })
      updateLabels()
    },
    searchInput,
  }
}
